import React, { useEffect, useState, useMemo } from "react";
import MultiSelectCheckboxGroup from "../../components/CheckBoxFilter";
import VerticalTable from "../../components/verticalTable";
import LineChart from "../../components/chartTypes/lineChart";
import { useTranslation } from "react-i18next";
import {
  sixMonthsAgoFormatted,
  twoDaysAgoFormatted,
} from "../../constants/intex";
import { flowRatesByBranchEwi } from "../../services/flowRates";
import { listBranches } from "../../services/filterApis";
import VerticalTable3 from "../../components/verticalTable3";

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type TableRow = {
  category: string;
  values: string[];
};

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const FlowRatesByBranch = () => {
  const { t } = useTranslation();

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));

  const [pendingFilters, setPendingFilters] = useState<
    Record<string, string[]>
  >(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));

  const [branchList, setBranchList] = useState<
    { branch_id: string; branch_name: string }[]
  >([]);
  const [applyFilter, setApplyFilter] = useState(0);
  const [loading, setLoading] = useState(false);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});

  // Consolidated data states
  const [sampleData, setSampleData] = useState<TableRow[]>([]);
  const [period, setPeriod] = useState<string[]>([]);

  // Comparative data states
  const [comparativeSampleData, setComparativeSampleData] = useState<
    TableRow[]
  >([]);
  const [comparativePeriod, setComparativePeriod] = useState<string[]>([]);

  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch") },
    {
      key: "periodWithOutDay",
      type: "singleSelect",
      label: t("filters.period"),
      isDisabled: true,
    },
    {
      key: "fromDate",
      type: "date",
      label: t("filters.fromDate"),
      maxValue: pendingFilters.toDate,
    },
    {
      key: "toDate",
      type: "date",
      label: t("filters.toDate"),
      maxValue: twoDaysAgoFormatted,
    },
  ];

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    branchList.forEach((branch) => {
      const id = branch?.branch_id;
      const name = branch?.branch_name;
      if (id && name) {
        map[id] = name;
      }
    });
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    return branchIdToNameMap[branchId] || branchId;
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const handleCancelClick = () => {
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
    setApplyFilter(0);
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
    setValidationError(null);
    setIsValid(true);
    setIsDownloadDisabled(false);
  };

  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
    }

    setSelectedFilters(pendingFilters);
    setValidationError(null);
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  const transformData = (apiData: any) => {
    const periodData = ["", ...apiData.map((obj: any) => obj.period)];
    const flowMap = new Map<string, string[]>();

    apiData.forEach((periodEntry: any) => {
      periodEntry.flow_bucket.forEach((bucket: any) => {
        const rawFlow = bucket?.flow;
        const flowLabel = rawFlow
          ? rawFlow
              .trim()
              .toLowerCase()
              .replace(/\b\w/g, (c: any) => c.toUpperCase())
          : "Unknown";

        if (!flowMap.has(flowLabel)) {
          flowMap.set(flowLabel, []);
        }

        flowMap
          .get(flowLabel)
          ?.push(bucket.flow_value_percentage?.toString() ?? "0");
      });
    });

    const transformedData = Array.from(flowMap.entries()).map(
      ([category, values]) => ({
        category,
        values,
      })
    );

    return { data: transformedData, period: periodData };
  };

  const transformComparativeData = (
    apiDataArray: any[],
    branchIds: string[]
  ) => {
    if (!apiDataArray || apiDataArray.length === 0) {
      return {
        data: [],
        period: [],
      };
    }

    // Get all unique flow categories from all branches
    const allFlowCategories = Array.from(
      new Set(
        apiDataArray.flatMap((data) =>
          data.flatMap((period: any) =>
            period.flow_bucket.map((bucket: any) =>
              bucket.flow
                ?.trim()
                .toLowerCase()
                .replace(/\b\w/g, (c: any) => c.toUpperCase())
            )
          )
        )
      )
    ).filter(Boolean); // Remove any undefined/null values

    // Get periods from first branch
    const periods = apiDataArray[0].map((period: any) => period.period);

    // Create headers with branch names for each period
    const periodData = [
      "",
      ...periods.flatMap((period: string) =>
        branchIds.map((branchId) => `${period}\n(${getBranchName(branchId)})`)
      ),
    ];

    // Initialize data
    const comparativeData: TableRow[] = [];

    allFlowCategories.forEach((flowCategory: string) => {
      const values: string[] = [];

      // For each period
      periods.forEach((period: string, periodIdx: number) => {
        // For each branch
        apiDataArray.forEach((branchData: any, branchIdx: number) => {
          const periodEntry = branchData[periodIdx];
          const bucket = periodEntry?.flow_bucket?.find(
            (b: any) =>
              b.flow
                ?.trim()
                .toLowerCase()
                .replace(/\b\w/g, (c: any) => c.toUpperCase()) === flowCategory
          );

          if (bucket) {
            values.push(bucket.flow_value_percentage.toString());
          } else {
            values.push("0");
          }
        });
      });

      comparativeData.push({
        category: flowCategory,
        values,
      });
    });

    // Assign colors to branches
    const colors: Record<string, BranchColor> = {};
    branchIds.forEach((branchId, index) => {
      colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
    });
    setBranchColors(colors);

    return { data: comparativeData, period: periodData };
  };

  const generateFlowRatesByBranchEwi = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      setComparativeLoading(true);
      try {
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map((branchId) =>
          flowRatesByBranchEwi({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);

        const transformed = transformComparativeData(
          branchResponses.map((r) => r.data),
          selectedBranches
        );

        setComparativeSampleData(transformed.data);
        setComparativePeriod(transformed.period);
      } catch (err) {
        setError("Failed to fetch comparative flow rates data");
        console.error(err);
      } finally {
        setComparativeLoading(false);
      }
    } else {
      setLoading(true);
      try {
        const response = await flowRatesByBranchEwi(selectedFilters);
        const transformed = transformData(response.data);

        setSampleData(transformed.data);
        setPeriod(transformed.period);
      } catch (err) {
        setError("Failed to fetch flow rates data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    generateFlowRatesByBranchEwi();
  }, [applyFilter]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const getColorForCategory = (category: string): string => {
    const colorMap: Record<string, string> = {
      "Good": "#4CAF50",  // Green
      "Bad": "#F44336",   // Red
      "Medium": "#FFC107", // Amber
      "Unknown": "#9E9E9E" // Grey
    };

    const normalizedCategory = category.toLowerCase();
    if (normalizedCategory.includes("good")) return colorMap["Good"];
    if (normalizedCategory.includes("bad")) return colorMap["Bad"];
    if (normalizedCategory.includes("medium")) return colorMap["Medium"];
    
    return colorMap[category] || 
      '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
  };

  const isComparativeMode =
    selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2";

  return (
    <div>
      <div className="page-content">
        <div className="header">{t("flowratesBranch.pageTitle")}</div>

        <div className="content px-3">
          <MultiSelectCheckboxGroup
            filters={filters}
            selectedValues={pendingFilters}
            onChange={handleFilterChange}
            isLoading={[loading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: handleApplyClick },
              { label: t("buttons.cancel"), onClick: handleCancelClick },
              {
                label: t("buttons.download"),
                onClick: () => alert("Download triggered"),
                disabled: isDownloadDisabled,
              },
            ]}
          />

          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>
        {!validationError && (
          <div className="p-2 me-3">
            <h2
              style={{
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "Arial",
                fontStyle: "normal",
                textAlign: "start",
                paddingLeft: "15px",
                color: "#14213d",
              }}
            >
              {t("flowratesBranch.Flow Rates By Branch EWI")}
            </h2>

            {applyFilter && !validationError && isComparativeMode ? (
              <VerticalTable3
                headers={comparativePeriod}
                data={comparativeSampleData}
                loading={comparativeLoading}
                isComparative={true}
                branchCount={selectedFilters.branch?.length || 0}
                branchColors={branchColors}
                branchIds={selectedFilters.branch || []}
              />
            ) : (
              <VerticalTable
                headers={period}
                data={sampleData}
                loading={loading}
              />
            )}
          </div>
        )}

        {!validationError && (
          <div className="mt-1 p-2">
            <LineChart
              title={t("flowratesBranch.title")}
              labels={period.slice(1)}
              dataSets={sampleData.map((item) => ({
                label: item.category,
                data: item.values.map((value) => parseFloat(value)),
                borderColor: getColorForCategory(item.category),
                backgroundColor: getColorForCategory(item.category),
              }))}
              xLabel={t("flowratesBranch.xLabel")}
              yLabel={t("flowratesBranch.yLabel")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowRatesByBranch;